import { createContext } from "react";
import { IdPLogoutOptions, IdPTokens, IdPUser, LoginWithIdPOptions } from "./types";

/**
 * The IdP session state.
 */
export interface IdPAuthState {
  /**
   * isAuthenticated returns true if the user holds a session with the IdP.
   */
  isAuthenticated: boolean;
  /**
   * isLoading returns true while the IdP client is starting up or handling the callback.
   */
  isLoading: boolean;
  /**
   * The signed-in IdP user, if any.
   */
  user?: IdPUser;
}

export interface IdPAuthContextInterface extends IdPAuthState {
  /**
   * ```js
   * await idp.loginWithRedirect(options);
   * ```
   * Builds the authorize request from the options and the current URL, then sends the browser to
   * the IdP. With `silent: true` it sends `prompt=none`, so the IdP answers without showing a page.
   */
  loginWithRedirect: (opts?: LoginWithIdPOptions) => Promise<void>;

  /**
   * ```js
   * const tokens = await idp.getHeldTokens();
   * ```
   * Returns the access and ID tokens already held, or `undefined` when none are. It makes no
   * network request; start a silent `loginWithRedirect` to get new tokens.
   */
  getHeldTokens: () => Promise<IdPTokens | undefined>;

  /**
   * ```js
   * await idp.logout({ returnTo });
   * ```
   * Ends the IdP session.
   */
  logout: (opts: IdPLogoutOptions) => Promise<void>;
}

export const initialIdPAuthState: IdPAuthState = {
  isAuthenticated: false,
  isLoading: false,
};

/**
 * @ignore
 */
const stub = (): never => {
  throw new Error("You forgot to wrap your component in <IdPAuthProvider>.");
};

/**
 * @ignore
 */
export const initialIdPAuthContext: IdPAuthContextInterface = {
  ...initialIdPAuthState,
  loginWithRedirect: stub,
  getHeldTokens: stub,
  logout: stub,
};

export const IdPAuthContext = createContext<IdPAuthContextInterface>(initialIdPAuthContext);
